// Load matching route drawer (ST-00218 P2), part (c): how long loads take on this route — a
// histogram of the matched loads' transit time (one-hour bins, drawn to scale on a numeric
// hour axis, stacked by tier), the transit window W the engine uses (learned / frozen / set by
// hand) and, for load_matching.manage, the override field. Every value is also reachable as
// a table ("Show as table"); the chart's hover only enhances.
import { useMemo, useState, type FormEvent } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import { useThemeTokens } from '../../../../utils/themeTokens';
import { formatMiningDate } from '../../../../utils/dateUtils';
import { loadMatchingErrorText, setWindowOverride } from '../../../../services/loadMatching';
import type { RouteWindow, Tier, TransitPanel } from '../../../../types/loadMatching';
import { TIER_LABELS, TIERS, WINDOW_SOURCE_LABELS, fmtMinutes, fmtN } from './format';
import { TierLegend, useTierColors } from './parts';

interface Row {
  /** bin centre in hours (x position) */
  x: number;
  from: number;
  to: number;
  t1: number;
  t2: number;
  t3: number;
  t4: number;
  total: number;
}

const tierKey = (t: Tier) => `t${t}` as 't1' | 't2' | 't3' | 't4';

/** Tick step (hours) that keeps ~6–12 ticks across the axis. */
const tickStep = (maxH: number): number =>
  maxH <= 12 ? 1 : maxH <= 24 ? 3 : maxH <= 48 ? 6 : maxH <= 96 ? 12 : 24;

function BinTip({ active, payload }: TooltipProps<number, string>) {
  const t = useThemeTokens();
  const colors = useTierColors();
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as Row | undefined;
  if (!row) return null;
  return (
    <div
      className="rounded-md px-2.5 py-2 text-[11px] shadow-lg"
      style={{
        background: t.tooltipBg,
        border: `1px solid ${t.tooltipBorder}`,
        color: t.tooltipText,
      }}
    >
      <div className="font-semibold mb-1">
        {fmtMinutes(row.from)} – {fmtMinutes(row.to)}: {fmtN(row.total)} loads
      </div>
      {TIERS.filter((tier) => row[tierKey(tier)] > 0).map((tier) => (
        <div key={tier} className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: colors[tier] }} />
          <span>
            {TIER_LABELS[tier].short}: {fmtN(row[tierKey(tier)])}
          </span>
        </div>
      ))}
    </div>
  );
}

export function TransitHistogram({ transit }: { transit: TransitPanel }) {
  const t = useThemeTokens();
  const colors = useTierColors();
  const [asTable, setAsTable] = useState(false);
  const rows: Row[] = useMemo(
    () =>
      transit.bins.map((b) => ({
        x: (b.fromMinutes + b.toMinutes) / 120,
        from: b.fromMinutes,
        to: b.toMinutes,
        t1: b.t1,
        t2: b.t2,
        t3: b.t3,
        t4: b.t4,
        total: b.t1 + b.t2 + b.t3 + b.t4,
      })),
    [transit.bins]
  );
  const maxH = transit.bins.length
    ? (transit.bins[transit.bins.length - 1]?.toMinutes ?? 0) / 60
    : 24;
  const step = tickStep(maxH);
  const ticks: number[] = [];
  for (let h = 0; h <= maxH + 1e-9; h += step) ticks.push(h);
  const w = transit.window;
  const wH = w.effectiveMinutes / 60;
  const learnedH =
    w.learnedMinutes !== null && w.learnedMinutes !== w.effectiveMinutes
      ? w.learnedMinutes / 60
      : null;
  const sum = (o: { t1: number; t2: number; t3: number; t4: number }) => o.t1 + o.t2 + o.t3 + o.t4;
  const plotted = rows.reduce((s, r) => s + r.total, 0);
  const overflow = sum(transit.overflow);
  const negative = sum(transit.negative);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <TierLegend />
        <button
          type="button"
          onClick={() => setAsTable((v) => !v)}
          className="text-[11px] font-medium text-primary-700 hover:underline"
        >
          {asTable ? 'Show as chart' : 'Show as table'}
        </button>
      </div>
      {plotted === 0 && !asTable ? (
        <p className="text-xs text-slate-500 py-6 text-center">
          No matched loads with a receipt time in this period.
        </p>
      ) : asTable ? (
        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md">
          <table className="w-full text-xs" aria-label="Transit time per hour">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold text-slate-600">Transit</th>
                {TIERS.map((tier) => (
                  <th key={tier} className="px-2 py-1.5 text-right font-semibold text-slate-600">
                    {TIER_LABELS[tier].short}
                  </th>
                ))}
                <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Loads</th>
              </tr>
            </thead>
            <tbody>
              {rows
                .filter((r) => r.total > 0)
                .map((r) => (
                  <tr key={r.from} className="border-t border-slate-100">
                    <td className="px-2 py-1 text-slate-700">
                      {fmtMinutes(r.from)} – {fmtMinutes(r.to)}
                    </td>
                    {TIERS.map((tier) => (
                      <td key={tier} className="px-2 py-1 text-right tabular-nums">
                        {fmtN(r[tierKey(tier)])}
                      </td>
                    ))}
                    <td className="px-2 py-1 text-right tabular-nums font-semibold">
                      {fmtN(r.total)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ height: 220 }} data-testid="transit-histogram">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              margin={{ top: 18, right: 12, left: 0, bottom: 4 }}
              barCategoryGap={1}
            >
              <CartesianGrid stroke={t.gridSubtle} vertical={false} />
              <XAxis
                type="number"
                dataKey="x"
                domain={[0, maxH]}
                ticks={ticks}
                tickFormatter={(h: number) => `${h} h`}
                tick={{ fontSize: 10, fill: t.axisLabel }}
                axisLine={{ stroke: t.grid }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 10, fill: t.axisLabel }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip content={<BinTip />} cursor={{ fill: t.cursor }} />
              {TIERS.map((tier) => (
                <Bar
                  key={tier}
                  dataKey={tierKey(tier)}
                  stackId="tiers"
                  fill={colors[tier]}
                  isAnimationActive={false}
                />
              ))}
              {learnedH !== null && (
                <ReferenceLine
                  x={learnedH}
                  stroke={t.reference}
                  strokeWidth={1}
                  label={{
                    value: `learned ${fmtMinutes(w.learnedMinutes)}`,
                    position: 'insideTopLeft',
                    fontSize: 10,
                    fill: t.axisLabel,
                  }}
                />
              )}
              <ReferenceLine
                x={wH}
                stroke={t.textStrong}
                strokeWidth={1.5}
                label={{
                  value: `window ${fmtMinutes(w.effectiveMinutes)}`,
                  position: 'top',
                  fontSize: 10,
                  fill: t.textStrong,
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <ul className="mt-2 text-[11px] text-slate-500 space-y-0.5">
        <li>
          {fmtN(plotted)} matched loads received in this period, by transit time (one bar per hour).
          {overflow > 0 &&
            ` ${fmtN(overflow)} took longer than ${fmtMinutes(transit.overflow.fromMinutes)} (not drawn).`}
        </li>
        {negative > 0 && (
          <li>
            {fmtN(negative)} receipts are timed before their dispatch (clock difference between the
            sites).
          </li>
        )}
        {transit.dateOnlyExcluded > 0 && (
          <li>
            {fmtN(transit.dateOnlyExcluded)} receipts have a date but no time, so they are left out
            of the chart.
          </li>
        )}
      </ul>
    </div>
  );
}

const statLine = (w: RouteWindow): string | null => {
  const s = w.sample;
  if (!s.n) return null;
  const parts = [
    s.p50Minutes !== null ? `half arrive within ${fmtMinutes(s.p50Minutes)}` : null,
    s.p95Minutes !== null ? `95% within ${fmtMinutes(s.p95Minutes)}` : null,
    s.p999Minutes !== null ? `99.9% within ${fmtMinutes(s.p999Minutes)}` : null,
    s.maxMinutes !== null ? `longest ${fmtMinutes(s.maxMinutes)}` : null,
  ].filter(Boolean);
  // `to` is the exclusive end (00:00 of the next day): show the last day it covers.
  const range =
    s.from && s.to
      ? ` (${fmtN(s.n)} loads, ${formatMiningDate(s.from)} – ${formatMiningDate(Date.parse(s.to) - 1)})`
      : ` (${fmtN(s.n)} loads)`;
  return `${parts.join(', ')}${range}.`;
};

/** The window W: what the engine uses, where it comes from, and (manage) the override field. */
export function WindowPanel({
  window: w,
  groupingId,
  canManage,
  onSaved,
}: {
  window: RouteWindow;
  groupingId: number;
  canManage: boolean;
  onSaved: (w: RouteWindow, queued: boolean) => void;
}) {
  const [hours, setHours] = useState(
    w.overrideMinutes !== null ? String(+(w.overrideMinutes / 60).toFixed(2)) : ''
  );
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const minH = w.limits.minMinutes / 60;
  const maxH = w.limits.maxMinutes / 60;
  const stats = statLine(w);

  const save = async (minutes: number | null) => {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const res = await setWindowOverride(groupingId, minutes, note.trim() || null);
      onSaved(res.window, res.rebuild.queued);
      setSaved(
        minutes === null
          ? 'Override cleared. The matching is being recalculated in the background; refresh in a few minutes.'
          : `Window set to ${fmtMinutes(minutes)}. The matching is being recalculated in the background; refresh in a few minutes.`
      );
      if (minutes === null) setHours('');
      setNote('');
    } catch (err) {
      setError(loadMatchingErrorText(err, 'The window could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const h = Number(hours);
    if (!Number.isFinite(h) || h < minH || h > maxH) {
      setError(`Enter a number of hours from ${minH} to ${maxH}.`);
      return;
    }
    void save(Math.round(h * 60));
  };

  return (
    <div
      className="rounded-md border border-slate-200 px-3 py-3 space-y-2"
      data-testid="window-panel"
    >
      <p className="text-sm text-slate-800">
        <span className="font-semibold">Transit window: {fmtMinutes(w.effectiveMinutes)}</span>{' '}
        <span className="text-slate-500">({WINDOW_SOURCE_LABELS[w.source]})</span>
      </p>
      <p className="text-xs text-slate-600">
        A load not received within this time counts as missing (if the receiving site has data for
        that time). Receipts later than this are flagged late.
      </p>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div>
          <dt className="text-slate-500">Learned</dt>
          <dd className="font-medium text-slate-800">
            {w.learnedMinutes !== null ? fmtMinutes(w.learnedMinutes) : 'not enough loads'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Frozen at switch-over</dt>
          <dd className="font-medium text-slate-800">{fmtMinutes(w.frozenMinutes)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Set by hand</dt>
          <dd className="font-medium text-slate-800">{fmtMinutes(w.overrideMinutes)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Suggested</dt>
          <dd className="font-medium text-slate-800">{fmtMinutes(w.suggestedMinutes)}</dd>
        </div>
      </dl>
      {stats && <p className="text-[11px] text-slate-500">Matched loads on this route: {stats}</p>}
      {w.dateOnlyReceipts && (
        <p className="text-[11px] text-slate-500">
          Receipts on this route carry a date only, so transit is counted in whole days.
        </p>
      )}
      {canManage && (
        <form
          noValidate
          onSubmit={submit}
          className="pt-2 border-t border-slate-100 flex flex-wrap items-end gap-2"
        >
          <div>
            <label
              htmlFor={`lm-window-${groupingId}`}
              className="block text-[11px] font-medium text-slate-600 mb-1"
            >
              Set the window by hand (hours)
            </label>
            <input
              id={`lm-window-${groupingId}`}
              type="number"
              inputMode="decimal"
              min={minH}
              max={maxH}
              step={0.5}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder={String(w.effectiveMinutes / 60)}
              className="w-28 px-2 py-1.5 border border-slate-300 rounded-md bg-surface text-sm text-slate-900 focus:outline-none focus:border-primary-500"
            />
          </div>
          <div className="flex-1 min-w-[12rem]">
            <label
              htmlFor={`lm-window-note-${groupingId}`}
              className="block text-[11px] font-medium text-slate-600 mb-1"
            >
              Why (optional, kept with the change)
            </label>
            <input
              id={`lm-window-note-${groupingId}`}
              type="text"
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Rail siding offloads the next morning"
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md bg-surface text-sm text-slate-900 focus:outline-none focus:border-primary-500"
            />
          </div>
          <button
            type="submit"
            disabled={busy || hours === ''}
            className="h-8 px-3 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            {busy ? 'Saving…' : 'Save window'}
          </button>
          {w.overrideMinutes !== null && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void save(null)}
              className="h-8 px-3 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Use the learned window
            </button>
          )}
          <p className="w-full text-[11px] text-slate-500">
            From {minH} to {maxH} hours. Saving recalculates the matching in the background (a few
            minutes).
          </p>
        </form>
      )}
      {saved && <p className="text-xs text-emerald-700">{saved}</p>}
      {error && (
        <p role="alert" className="text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
